import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Loader2, CheckCircle2, AlertCircle, ChevronDown, X,
  Users, Globe, Plus, Check
} from 'lucide-react';
import { countries } from '../dashboard/CountryDropdown';
import FlagImage from '../common/FlagImage';

/**
 * Assign Brands (admin only).
 *
 * Every brand in the database can be handed to any account, in any of the
 * three roles it can hold one: a project of their own, a Competitor Spy
 * target, or an Affiliate Gap comparison — which needs a second brand, the
 * account's own, to measure the gap against.
 *
 * Nothing is copied but the brand row itself. Affiliates live in
 * affiliate_clean keyed by brand_url, so the account sees every affiliate
 * already discovered for that brand the moment the row exists; a background
 * live check then re-verifies the inherited list (the bulb on their card).
 *
 * Countries can be set per assignment and changed afterwards — they decide
 * which SEMrush market the brand's numbers come from, so two accounts can hold
 * the same brand in different markets.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const BASE = `${API_URL}/api/admin/brand-assign`;

const VERTICALS = ['Casino', 'Sports Betting', 'Poker', 'Lottery', 'Bingo', 'Sweepstake', 'Crypto'];

const ROLES = [
  { key: 'project', label: 'Project', hint: 'Shows on their dashboard as their own brand' },
  { key: 'competitor', label: 'Competitor', hint: 'Shows in Competitor Spy' },
  { key: 'gap', label: 'Affiliate Gap', hint: "Needs one of their own brands to compare against" }
];

const request = async (path, options = {}) => {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...options
  });
  return response.json();
};

// Multi-select over the shared country list. Codes are stored uppercase, the
// way every add-brand form writes them.
const CountryPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries.slice(0, 60);
    return countries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)).slice(0, 60);
  }, [search]);

  const toggle = (code) => {
    onChange(value.includes(code) ? value.filter(c => c !== code) : [...value, code]);
  };

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setOpen(o => !o)}
        className="min-h-[42px] flex flex-wrap items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg cursor-pointer"
      >
        {value.length === 0 && <span className="text-sm text-gray-400 dark:text-slate-500">Any country</span>}
        {value.map(code => (
          <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-full text-xs text-gray-700 dark:text-slate-200">
            <FlagImage countryCode={code} size={12} />
            {code}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggle(code); }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100 dark:border-slate-800">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries"
              className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.map(country => (
              <button
                key={country.code}
                type="button"
                onClick={() => toggle(country.code)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                <FlagImage countryCode={country.code} size={14} />
                <span className="text-gray-700 dark:text-slate-200">{country.name}</span>
                <span className="text-gray-400 dark:text-slate-500 text-xs">{country.code}</span>
                {value.includes(country.code) && <Check className="w-4 h-4 text-blue-500 ml-auto" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const RoleBadge = ({ role }) => {
  const styles = {
    project: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    competitor: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    gap: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
  };
  const labels = { project: 'Project', competitor: 'Competitor', gap: 'Gap' };
  return <span className={`px-2 py-0.5 rounded-full text-xs ${styles[role]}`}>{labels[role]}</span>;
};

const AssignBrands = () => {
  const [brandSearch, setBrandSearch] = useState('');
  const [brands, setBrands] = useState([]);
  const [brandTotal, setBrandTotal] = useState(0);
  const [brandOffset, setBrandOffset] = useState(0);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBrands, setUserBrands] = useState([]);

  const [role, setRole] = useState('project');
  const [ownBrandUrl, setOwnBrandUrl] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedVerticals, setSelectedVerticals] = useState([]);

  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  // Inline country editing on a brand the account already holds
  const [editingId, setEditingId] = useState(null);
  const [editCountries, setEditCountries] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const PAGE = 50;

  const loadBrands = useCallback(async (search, offset) => {
    setLoadingBrands(true);
    try {
      const data = await request(`/brands?search=${encodeURIComponent(search)}&limit=${PAGE}&offset=${offset}`);
      if (data.success) {
        setBrands(prev => (offset === 0 ? data.brands : [...prev, ...data.brands]));
        setBrandTotal(data.total || 0);
        setBrandOffset(offset);
      } else {
        setError(data.message || 'Could not load brands');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  const loadUsers = useCallback(async (search) => {
    setLoadingUsers(true);
    try {
      const data = await request(`/users?search=${encodeURIComponent(search)}`);
      if (data.success) setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadUserBrands = useCallback(async (userId) => {
    try {
      const data = await request(`/users/${userId}`);
      if (data.success) setUserBrands(data.brands || []);
    } catch (err) {
      console.error('Failed to load account brands:', err);
    }
  }, []);

  // Debounced searches — typing a brand name should not fire a query per key.
  useEffect(() => {
    const t = setTimeout(() => loadBrands(brandSearch, 0), 300);
    return () => clearTimeout(t);
  }, [brandSearch, loadBrands]);

  useEffect(() => {
    const t = setTimeout(() => loadUsers(userSearch), 300);
    return () => clearTimeout(t);
  }, [userSearch, loadUsers]);

  useEffect(() => {
    if (selectedUser) loadUserBrands(selectedUser.id);
    else setUserBrands([]);
    setOwnBrandUrl('');
  }, [selectedUser, loadUserBrands]);

  // Default the countries to whatever the brand already carries, so assigning
  // without touching them keeps the brand as it is.
  useEffect(() => {
    if (!selectedBrand) return;
    const existing = (selectedBrand.country || '')
      .split(',').map(c => c.trim().toUpperCase()).filter(Boolean);
    setSelectedCountries(existing);
    setSelectedVerticals([]);
  }, [selectedBrand]);

  const ownProjectBrands = userBrands.filter(b => b.role === 'project');

  const handleAssign = async () => {
    if (!selectedBrand || !selectedUser) {
      setError('Pick a brand and an account');
      return;
    }
    if (role === 'gap' && !ownBrandUrl) {
      setError("Affiliate Gap needs one of the account's own brands to compare against");
      return;
    }

    setAssigning(true);
    setError('');
    setResult('');
    try {
      const data = await request('/assign', {
        method: 'POST',
        body: JSON.stringify({
          brandUrl: selectedBrand.brand_url,
          userId: selectedUser.id,
          role,
          userBrandUrl: role === 'gap' ? ownBrandUrl : undefined,
          countries: selectedCountries,
          verticals: selectedVerticals
        })
      });

      if (data.success) {
        setResult(data.message);
        loadUserBrands(selectedUser.id);
      } else {
        setError(data.message || 'Could not assign that brand');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const startEdit = (brand) => {
    setEditingId(brand.id);
    setEditCountries((brand.country || '').split(',').map(c => c.trim().toUpperCase()).filter(Boolean));
  };

  const saveEdit = async (brandRowId) => {
    setSavingEdit(true);
    try {
      const data = await request(`/brand/${brandRowId}`, {
        method: 'PATCH',
        body: JSON.stringify({ countries: editCountries })
      });
      if (data.success) {
        setEditingId(null);
        loadUserBrands(selectedUser.id);
      } else {
        setError(data.message || 'Could not change the countries');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Assign Brands</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
          Give any brand in the database to any account — as one of their projects, as a
          Competitor Spy target, or as an Affiliate Gap against a brand they already have.
          They get every affiliate already found for it straight away.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ---- Brand picker ---- */}
        <div className="p-5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Brands in the database
            </h2>
            <span className="text-xs text-gray-400 dark:text-slate-500">{brandTotal} total</span>
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder="Search by name or URL"
              className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white"
            />
          </div>

          <div className="max-h-[420px] overflow-y-auto -mx-2 px-2">
            {loadingBrands && brands.length === 0 && (
              <div className="py-6 text-center text-sm text-gray-500 dark:text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading
              </div>
            )}
            {brands.map(brand => {
              const active = selectedBrand?.brand_url === brand.brand_url;
              return (
                <button
                  key={brand.brand_url}
                  type="button"
                  onClick={() => setSelectedBrand(brand)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 border ${
                    active
                      ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-900 dark:text-white truncate">
                        {brand.brand_name || brand.brand_url}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-slate-500 truncate">{brand.brand_url}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 text-xs">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 rounded-full">
                        {brand.affiliates} affiliates
                      </span>
                      <span className="inline-flex items-center gap-1 text-gray-400 dark:text-slate-500">
                        <Users className="w-3 h-3" />{brand.accounts}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
            {brands.length < brandTotal && (
              <button
                type="button"
                onClick={() => loadBrands(brandSearch, brandOffset + PAGE)}
                disabled={loadingBrands}
                className="w-full mt-2 py-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                {loadingBrands ? 'Loading…' : `Load more (${brandTotal - brands.length} left)`}
              </button>
            )}
          </div>
        </div>

        {/* ---- Assignment ---- */}
        <div className="p-5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl">
          <h2 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
            {selectedBrand
              ? `Give ${selectedBrand.brand_name || selectedBrand.brand_url} to an account`
              : 'Pick a brand on the left'}
          </h2>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search accounts by email, name or company"
              className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white"
            />
          </div>

          <div className="max-h-40 overflow-y-auto -mx-2 px-2 mb-4">
            {loadingUsers && users.length === 0 && (
              <div className="py-4 text-center text-sm text-gray-500 dark:text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading
              </div>
            )}
            {users.map(u => {
              const active = selectedUser?.id === u.id;
              const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setSelectedUser(u)}
                  className={`w-full text-left px-3 py-2 rounded-lg mb-1 border ${
                    active
                      ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-900 dark:text-white truncate">{u.business_email}</div>
                      <div className="text-xs text-gray-400 dark:text-slate-500 truncate">
                        {name || u.company_name || '—'} · {u.plan || 'free'}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 dark:text-slate-500 flex-shrink-0">
                      {u.total_brand}/{u.brand_limit ?? '–'} brands
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            {ROLES.map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRole(r.key)}
                title={r.hint}
                className={`px-3 py-2 rounded-lg text-sm border ${
                  role === r.key
                    ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {role === 'gap' && (
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
                Compare against (their own brand)
              </label>
              <select
                value={ownBrandUrl}
                onChange={(e) => setOwnBrandUrl(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white"
              >
                <option value="">
                  {ownProjectBrands.length === 0 ? 'This account has no brands of its own yet' : 'Select their brand'}
                </option>
                {ownProjectBrands.map(b => (
                  <option key={b.id} value={b.brand_url}>{b.brand_name} — {b.brand_url}</option>
                ))}
              </select>
              {ownProjectBrands.length === 0 && selectedUser && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Give them a Project brand first — a gap is measured between two brands.
                </p>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              <Globe className="w-3 h-3 inline mr-1" />
              Countries
            </label>
            <CountryPicker value={selectedCountries} onChange={setSelectedCountries} />
            <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
              Decides which market its numbers come from. Defaults to what the brand already has.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Verticals <span className="text-gray-400 dark:text-slate-500">(optional)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {VERTICALS.map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setSelectedVerticals(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
                  className={`px-2.5 py-1 rounded-full text-xs border ${
                    selectedVerticals.includes(v)
                      ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="mb-3 flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{error}
            </div>
          )}
          {result && !error && (
            <div className="mb-3 flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />{result}
            </div>
          )}

          <button
            type="button"
            onClick={handleAssign}
            disabled={assigning || !selectedBrand || !selectedUser}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Assign brand
          </button>

          {/* ---- What the account already holds ---- */}
          {selectedUser && (
            <div className="mt-6 pt-5 border-t border-gray-100 dark:border-slate-800">
              <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                {selectedUser.business_email} has {userBrands.length} brand{userBrands.length === 1 ? '' : 's'}
              </h3>
              <div className="max-h-72 overflow-y-auto space-y-2">
                {userBrands.map(b => (
                  <div key={b.id} className="px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900 dark:text-white truncate">{b.brand_name}</span>
                          <RoleBadge role={b.role} />
                        </div>
                        <div className="text-xs text-gray-400 dark:text-slate-500 truncate">
                          {b.brand_url}
                          {b.role === 'gap' && b.user_brand_url ? ` vs ${b.user_brand_url}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-500 dark:text-slate-400">{b.country || '—'}</span>
                        <button
                          type="button"
                          onClick={() => (editingId === b.id ? setEditingId(null) : startEdit(b))}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {editingId === b.id ? 'Cancel' : 'Change'}
                        </button>
                      </div>
                    </div>

                    {editingId === b.id && (
                      <div className="mt-2">
                        <CountryPicker value={editCountries} onChange={setEditCountries} />
                        <button
                          type="button"
                          onClick={() => saveEdit(b.id)}
                          disabled={savingEdit || editCountries.length === 0}
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-slate-700 text-white text-xs rounded-lg"
                        >
                          {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Save countries
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {userBrands.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-slate-400">Nothing yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignBrands;
